import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Galleria } from 'primereact/galleria'
import { UserPhoto,UserVideoInterview} from '../../../../../../../common/types/user'

interface PhotoGalleryProps {
  children?: React.ReactNode
  imgInnerInformation?: React.ReactNode
  photos: UserPhoto[]
  videos: UserVideoInterview[] | null
}

const interleave = <a,b>(xs:a[],ys:b[],which:'x' | 'y'='x') : (a | b)[] => {
  switch(which)
  {
    case 'x':
      return (
        xs.length > 0
        ? ([xs[0]] as (a | b)[]).concat(interleave(xs.slice(1),ys,'y'))
        : ys.length > 0
        ? interleave(xs,ys,'y')
        : []
      )
    case 'y':
      return (
        ys.length > 0
        ? ([ys[0]] as (a | b)[]).concat(interleave(xs,ys.slice(1),'x'))
        : xs.length > 0
        ? interleave(xs,ys,'x')
        : []
      )
  }
}

const PhotoGallery: React.FC<PhotoGalleryProps> = ({ children,...props }) => {
  const navigate = useNavigate()
  const items = interleave(
    props.photos.sort((a,b) => a.sort - b.sort).map((x) => ({ 'url':x.imgUrl,'sort':x.sort,'isVideo':false })),
    props.videos === null
    ? [{ 'url':'','sort':0,'isVideo':null as boolean | null,'overlayText':'' }]
    : props.videos.sort((a,b) => a.sort - b.sort).map((x) => ({ 'url':x.url,'sort':x.sort,'isVideo':true,'overlayText':x.question.question }))
  )
  const [activeIndex, setActiveIndex] = useState(0)
  const responsiveOptions = [
    { breakpoint: '991px', numVisible: 4 },
    { breakpoint: '767px', numVisible: 3 },
    { breakpoint: '575px', numVisible: 1 },
  ]

  const next = () => {
    setActiveIndex(prevState => (prevState === items.length - 1) ? 0 : prevState + 1)
  }

  const prev = () => {
    setActiveIndex((prevState) => (prevState === 0 ? items.length - 1 : prevState - 1))
  }

  const itemTemplate = (item:{ url:string,imageAlt:string,isVideo:boolean | null,overlayText?:string }) => {

    const VideoTextOverlay = () => {
      return item.isVideo && (
        <div
          className="position-absolute start-50 translate-middle-x p-1 px-2 rounded-3 w-80 text-center question-disappear-animation"
          style={{
            'backgroundColor':'rgba(var(--bs-secondary-light-rgb), .6) !important',
            'top':'27px',
          }}
        >
          <h5 className='fw-bold text-black'>{item.overlayText}</h5>
        </div>
      )
    }

    return (
  <div className="image-container position-relative w-100">
    {item.isVideo === null ? (
      <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '500px' }}>
        <h6 className="text-center">
          It wouldn't be playing fair to show you someone's
          <br />
          videos when you don't have any yourself...
          <br />
          <button className="btn btn-primary" onClick={() => navigate('/profile/video-interviews')}>
            Upload your own to become a VIP!
          </button>
        </h6>
      </div>
    ) : item.isVideo ? (
      <>
        <video
			  className="user-video rounded-3 shadow"
			  src={item.url}
			  controls
			  controlsList="nodownload"
			  autoPlay
			  style={{
				transform: 'rotate(0deg)',
				transformOrigin: 'center',
				width: '100%',
				height: 'auto',
				display: 'block',
			  }}
			  data-rotation="0"
			/>
        <div className="text-center mt-2">
          <button
            className="btn btn-sm btn-secondary"
            onClick={(e) => {
			  const container = e.currentTarget.closest('.image-container') as HTMLElement;
			  const video = container?.querySelector('video') as HTMLVideoElement;

			  if (video) {
				let rotation = parseInt(video.dataset.rotation || '0', 10);
				rotation = (rotation + 90) % 360;
				video.style.transform = `rotate(${rotation}deg)`;
				video.dataset.rotation = rotation.toString();

				// Set width for vertical orientation
				if (rotation === 90 || rotation === 270) {
				  video.style.width = '350px';
				} else {
				  video.style.width = '100%';
				}
				video.style.height = 'auto';

				// Optional: adjust min-height of container
				setTimeout(() => {
				  const newHeight = video.getBoundingClientRect().height;
				  container.style.minHeight = `${newHeight}px`;
				}, 100);
			  }
			}}
          >
            Rotate Video
          </button>
        </div>
      </>
    ) : (
      <img className="user-image" src={item.url} alt={item.imageAlt} />
    )}

    <ImageCount index={items.findIndex((photo) => photo.url === item.url)} />

    <VideoTextOverlay />

    <span className="left-tap position-absolute top-50 start-0 translate-middle-y h-80" onClick={prev}>
      <i className="pi pi-chevron-left position-absolute top-50 start-0 translate-middle-y fs-1 bg-secondary-light rounded-5 p-2 mx-2 opacity-50 text-primary"></i>
    </span>

    <span className="right-tap position-absolute top-50 end-0 translate-middle-y h-80" onClick={next}>
      <i className="pi pi-chevron-right position-absolute top-50 end-0 translate-middle-y fs-1 bg-secondary-light rounded-5 p-2 mx-2 opacity-50 text-primary"></i>
    </span>
  </div>
);

  }

  const ImageCount = ({ index }: { index: number }) => {
    return (
      <h6 className="px-2 bg-gray position-absolute top-0 start-50 translate-middle-x rounded opacity-75 mt-1 text-white">
        {index + 1} of {items.length}
      </h6>
    )
  }

  return (
    <div className="photo-wrapper w-100 mb-2">
      <Galleria
        activeIndex={activeIndex}
        value={items}
        responsiveOptions={responsiveOptions}
        numVisible={2}
        item={itemTemplate}
        onItemChange={(e) => setActiveIndex(e.index)}
        showThumbnails={false}
        className='rounded-3 overflow-hidden shadow'
        // thumbnail={ImageThumbnailTemplate}
        // thumbnailsPosition="bottom"
      >
        {/* <div className="sticky-bottom bg-primary">{imgInnerInformation}</div> */}
      </Galleria>
      {children}
    </div>
  )
}

export default PhotoGallery
